import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { fetchCancellationLedger } from 'utils/apiHelper';

const formatAmount = (value) =>
  Math.abs(Number(value || 0)).toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2
  });

const formatCancellationType = (value) => {
  if (value === 'sale_return') return 'Sale Return';
  if (value === 'edit') return 'Edit';
  return 'Cancellation';
};

export default function CancellationLedgerModal({ invoiceNumber, open, onClose }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !invoiceNumber) return undefined;

    let active = true;
    setLoading(true);
    setError('');
    fetchCancellationLedger('', null, true, null, invoiceNumber, null)
      .then((data) => {
        if (active) setRecords(data?.results || []);
      })
      .catch(() => {
        if (active) {
          setRecords([]);
          setError('Unable to load cancellation history.');
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [invoiceNumber, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Cancellation History: {invoiceNumber}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 5 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : error ? (
          <Typography color="error" sx={{ py: 2 }}>{error}</Typography>
        ) : records.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>No cancellation ledger entries found.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date &amp; Time</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Processed By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>{new Date(record.created_at).toLocaleString('en-IN')}</TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" label={formatCancellationType(record.cancellation_type)} />
                    </TableCell>
                    <TableCell align="right">
                      <Typography color={Number(record.amount) >= 0 ? 'success.main' : 'error.main'} fontWeight={600}>
                        {formatAmount(record.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>{record.cancellation_created_by_name || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

CancellationLedgerModal.propTypes = {
  invoiceNumber: PropTypes.string,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};
